/* import __COLOCATED_TEMPLATE__ from './standard-modal-layout.hbs.js'; */
/**

## Modal Standard Layout

`StandardLayout` has all the basic elements of a modal.

## How to show a modal

1. Create a component, i.e. `{{my-modal}}`. 99.9% percent of the time, you will include
   the `{{deprecated/standard-modal-layout}}` component inside of it. Don't forget your `dialog--modal__content`
   div around the actual content.
2. In response to some user action, call `open('my-modal')` on the modal service.

    ```javascript
    // some other route or component
    modal: Ember.inject.service(),

    actions: {
      openModal() {
        this.get('modal').open('my-modal', { someData: 108 })
          .then(() =>
            // do something when the modal closes
          );
      }
    }
    ```

3. Your `my-modal` component will receive five properties:
    1. A `modalOptions` property, which is the second argument to `open()`.
       So you can access `someData` with `this.get('modalOptions.someData')`.
    2. A `close` action, which closes the modal.
    3. A `setCloseHandler` action, which allows the component to disallow closing
       the modal when clicking on the veil or pressing ESC.
    4. A `lockModalHeight` action, which allows the component to fix its vertical
       height before making changes to its contents, to avoid vertical bouncing.
    5. An `unlockModalHeight` action, which returns to the default vertical sizing.

```example
@title Basic Example
@description Note that the `modal-layout` div is a bit of a hack to visually illustrate how it looks. You don't actually define this div in your hbs file.

@hbs
{{#toggle-code-example exampleName="Standard Modal" show=showModal}}
  <div class="modal-layout">
    {{#deprecated/standard-modal-layout title=(t 'common.learnMore')
                              primaryButtonConfig=(hash label=(t 'button.ok') action=(action (mut showModal) false))
                              secondaryButtonConfig=(hash label=(t 'button.cancel') action=(action (mut showModal) false))}}

      <div class="dialog--modal__content">
        <!-- all your content for the Modal goes here -->
        <h3>Modal Body</h3>
      </div>

    {{/deprecated/standard-modal-layout}}
  </div>
{{/toggle-code-example}}
```

```example
@title Example with Flash Message

@controller-properties
{
  flashParams: {
    type: 'ERROR',
    message: 'Some example error message',
    isActive: true
  }
}

@hbs
{{#toggle-code-example exampleName="Standard Modal" show=showModal}}
  <div class="modal-layout">
    {{#deprecated/standard-modal-layout title=(t 'common.learnMore')
                              flashParams=flashParams
                              primaryButtonConfig=(hash label=(t 'button.ok') action=(action (mut showModal) false))
                              secondaryButtonConfig=(hash label=(t 'button.cancel') action=(action (mut showModal) false))}}

      <div class="dialog--modal__content">
        <!-- all your content for the Modal goes here -->
        <h3>Modal Body</h3>
      </div>

    {{/deprecated/standard-modal-layout}}
  </div>
{{/toggle-code-example}}
```

## Modal with Header Links

```example
@title Basic Example
@description Note that the `modal-layout` div is a bit of a hack to visually illustrate how it looks. You don't actually define this div in your hbs file.

@hbs
{{#toggle-code-example exampleName="Modal with Header Links" show=showModal}}
  <div class="modal-layout">
    {{#deprecated/standard-modal-layout title=(t 'common.learnMore')
                              headerLinkConfigs=(array (hash label="Close with Link"
                                                             action=(action (mut showModal) false)
                                                             isDisabled=false))
                              primaryButtonConfig=(hash label=(t 'button.ok') action=(action (mut showModal) false))}}

      <div class="modal-layout__subheader">
        <!-- all your subheader content goes here -->
        <p>{{t 'common.password.heading'}}</p>
      </div>

      <div class="dialog--modal__content">
        <!-- all your content for the Modal goes here -->
        <h3>Modal Body</h3>
      </div>

    {{/deprecated/standard-modal-layout}}
  </div>
{{/toggle-code-example}}
```

## Modal with Subheader

```example
@title Basic Example
@description Note that the `modal-layout` div is a bit of a hack to visually illustrate how it looks. You don't actually define this div in your hbs file.

@hbs
{{#toggle-code-example exampleName="Modal with Subheader" show=showModal}}
  <div class="modal-layout">
    {{#deprecated/standard-modal-layout title=(t 'common.learnMore')
                              primaryButtonConfig=(hash label=(t 'button.ok') action=(action (mut showModal) false))
                              secondaryButtonConfig=(hash label=(t 'button.cancel') action=(action (mut showModal) false))}}

      <div class="modal-layout__subheader">
        <!-- all your subheader content goes here -->
        <p>{{t 'common.password.heading'}}</p>
      </div>

      <div class="dialog--modal__content">
        <!-- all your content for the Modal goes here -->
        <h3>Modal Body</h3>
      </div>

    {{/deprecated/standard-modal-layout}}
  </div>
{{/toggle-code-example}}
```

## Subheader with traversal arrows

Add special `modal-layout__subheader-arrow`
links in the subheader for usage any way you see fit (traditionally pagination among multiple options.) If you add arrows,
also add `modal-layout__subheader--with-arrows` as a modifier class.

```example
@title Basic Example
@description Note that the `modal-layout` div is a bit of a hack to visually illustrate how it looks. You don't actually define this div in your hbs file.

@controller-properties
{
  currentItemNumber: 1,
  modalPrevDisabled: true,
  modalNextDisabled: false,
  updateArrowState() {
    // normally not needed, just use computed values, but not possible in Fuse Docs
    this.set('modalPrevDisabled', this.get('currentItemNumber') <= 1);
    this.set('modalNextDisabled', this.get('currentItemNumber') >= 5);
  },
  actions: {
    modalPrev() {
      if (this.get('modalPrevDisabled')) {
        return;
      }
      if (this.get('currentItemNumber') > 1) {
        this.set('currentItemNumber', this.get('currentItemNumber') - 1);
        this.updateArrowState();
      }
    },
    modalNext() {
      if (this.get('modalNextDisabled')) {
        return;
      }
      if (this.get('currentItemNumber') < 5) {
        this.set('currentItemNumber', this.get('currentItemNumber') + 1);
        this.updateArrowState();
      }
    }
  }
}

@hbs
{{#toggle-code-example exampleName="Modal with Subheader and Traversal Arrows" show=showModal}}
  <div class="modal-layout">
    {{#deprecated/standard-modal-layout title=(t 'common.learnMore')
                              primaryButtonConfig=(hash label=(t 'button.ok') action=(action (mut showModal) false))
                              secondaryButtonConfig=(hash label=(t 'button.cancel') action=(action (mut showModal) false))}}

      <div class="modal-layout__subheader modal-layout__subheader--with-arrows">
        <a class="modal-layout__subheader-arrow modal-layout__subheader-arrow--prev {{if modalPrevDisabled 'modal-layout__subheader-arrow--is-disabled'}}" href="#" {{action "modalPrev"}}>
          <span class="modal-layout__subheader-arrow-notch"></span>
          Previous
        </a>
        <a class="modal-layout__subheader-arrow modal-layout__subheader-arrow--next {{if modalNextDisabled 'modal-layout__subheader-arrow--is-disabled'}}" href="#" {{action "modalNext"}}>
          <span class="modal-layout__subheader-arrow-notch"></span>
          Next
        </a>
        <p>Item {{currentItemNumber}}</p>
      </div>

      <div class="dialog--modal__content">
        <!-- all your content for the Modal goes here -->
        <h3>Modal Body</h3>
      </div>

    {{/deprecated/standard-modal-layout}}
  </div>
{{/toggle-code-example}}
```

<a name="advanced"></a>
## Advanced techniques

### Disable closing

You may want to disallow the user from closing the modal for brief periods of time, like while a network request is in progress.

The first step is to disable the buttons:

```hbs
{{#deprecated/standard-modal-layout primaryButtonConfig=(hash ... isDisabled=isLoading)
                          secondaryButtonConfig=(hash ... isDisabled=isLoading)}}
```

This alone is not sufficient: you also need to disallow clicking outside the modal or pressing the ESC key. You can register a "close handler" function in your modal component like so:

```js
didInsertElement(...args) {
  this._super(...args);

  this.setCloseHandler(() => {
    if (this.get('isLoading')) {
      return false; // do not close
    }

    // allow closing and the promise does not resolve to a value
    // i.e.:
    //    this.get('modal').open('my-modal')
    //      .then(val => console.log('val is undefined'));
    return true;

    // or

    // allow closing with a resolved value
    // i.e.:
    //     this.get('modal').open('my-modal')
    //       .then(val => val.cancelled === true);
    return { cancelled: true };
  }));
}
```

### Customizing headers and footers

**First:** do you really need to? Because of the disruptive nature of modals, consistency in the user interface and interaction model is paramount.

The dialog window, header, footer, and flash message are implemented as reusable components, so if you need to customize one, you can reuse the unchanged components.

You can swap out `deprecated/standard-modal-header` or `deprecated/standard-modal-footer` with your custom implementation.

You'll need to add `div.dialog--modal__body` around the content area and any flash messages or subheaders.

```hbs
{{#deprecated/standard-modal-dialog}}
  {{myteam/custom-modal-header}}

  {{flash-portal context=flash.contexts.modal}}

  <div class="dialog--modal__body">
    <div class="model-layout__content">
      Content
    </div>
  </div>

  {{deprecated/standard-modal-footer isShowingSpinner=isLoading
                           primaryButtonConfig=(hash
                             label="OK"
                             action=(action "doSomething"))
                           secondaryButtonConfig=(hash
                             label="Cancel"
                             action=(action close))}}
{{/deprecated/standard-modal-dialog}}
```

@param {String} dialogClass - Class names applied to the modal dialog (applied to deprecated/standard-modal-dialog)
@param {Boolean} [hasDividers=true] - If true, shows lines above and below the content. Note that dividers always appear if the inner content scrolls (applied to deprecated/standard-modal-dialog)
@param {String} title - Localized string (applied to deprecated/standard-modal-header)
@param {String} helpText - (Optional) Localized string (applied to deprecated/standard-modal-header)
@param {Object} flashParams - Object with `type`, `message`, and `isActive` properties (typically from the flash_support mixin)
@param {Array} headerLinkConfigs - List of Objects with `label`, `action`, and `isDisabled` properties. Adds links to right side of header.
@param {Object} primaryButtonConfig - Object with `label` and `action` properties. Represents the bottom rightmost button (applied to deprecated/standard-modal-footer)
@param {Object} secondaryButtonConfig - See above. Represents the bottom left botton. Leave off to hide (applied to deprecated/standard-modal-footer)
@param {Object} tertiaryButtonConfig - See above. Represents the button to the left of primary in the bottom right corner. Leave off to hide (applied to deprecated/standard-modal-footer)
@param {Boolean} isShowingSpinner - If true, shows a spinner in the footer (applied to deprecated/standard-modal-footer)
@param {Boolean} shouldCenterButtons - If true, the buttons in the footer will be center-aligned
@param {String} autofocusSelector - If provided, autofocuses the actionable element found by the selector in the standard dialog. Otherwise the first actionable element is focused.

Sass dependencies at: `app/assets/stylesheets/bridgeport/components/_modal-layout.sass`
*/

import Component from '@ember/component';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  tagName: '',
  autofocusSelector: null,
  hasDividers: true,
  shouldCenterButtons: false
});